import { ref, computed } from 'vue';
import bondService from '@/service/bondService';
import { getTodayForAPI } from '@/utils/dateUtil';

export function useMsbBonds() {
  // State
  const msbBonds = ref([]);
  const bondTypes = ref([]); // 동적으로 로드될 타입들
  const isLoading = ref(false);

  // 기본 통안채 타입 (fallback)
  const defaultMsbTypes = {
    TD: '통당',
    GTD: '구통당',
    GGTD: '구구통당',
    ST: '삼통',
    GST: '구삼통',
    GGST: '구구삼통',
    ETC: '기타',
    UNCATEGORIZED: '미분류',
  };

  // Computed
  const msbTypes = computed(() => {
    if (bondTypes.value.length === 0) {
      return defaultMsbTypes;
    }

    const types = {};
    bondTypes.value.forEach(bondType => {
      types[bondType.type] = bondType.title;
    });
    return types;
  });

  const typeOrder = computed(() => {
    if (bondTypes.value.length === 0) {
      return Object.keys(defaultMsbTypes);
    }
    return bondTypes.value.map(bondType => bondType.type);
  });

  const groupedBonds = computed(() => {
    const grouped = {};

    // 모든 타입에 대해 빈 배열로 초기화
    typeOrder.value.forEach(type => {
      grouped[type] = [];
    });

    // 채권들을 타입별로 분류
    msbBonds.value.forEach(bond => {
      const type = bond.msbType || 'UNCATEGORIZED';
      if (grouped[type]) {
        grouped[type].push(bond);
      } else {
        // 예상치 못한 타입이 있다면 UNCATEGORIZED에 추가
        if (!grouped['UNCATEGORIZED']) {
          grouped['UNCATEGORIZED'] = [];
        }
        grouped['UNCATEGORIZED'].push(bond);
      }
    });

    return grouped;
  });

  // Methods
  const loadBondTypes = async () => {
    try {
      const response = await bondService.getMsbBondTypes();
      bondTypes.value = response || [];
    } catch (error) {
      console.error('통안채 타입 로드 실패:', error);
      // 에러 시 기본값 사용
      bondTypes.value = [];
    }
  };

  const loadMsbBonds = async (baseDate = null) => {
    try {
      isLoading.value = true;

      // 타입과 채권 데이터를 병렬로 로드
      await Promise.all([loadBondTypes(), loadBondData(baseDate)]);
    } catch (error) {
      console.error('통안채 데이터 로드 실패:', error);
    } finally {
      isLoading.value = false;
    }
  };

  const loadBondData = async (baseDate = null) => {
    if (!baseDate) {
      baseDate = getTodayForAPI();
    }

    const response = await bondService.getMsbBonds(baseDate);

    if (Array.isArray(response)) {
      msbBonds.value = response;
    } else {
      console.error('응답이 배열이 아닙니다:', response);
      msbBonds.value = [];
    }
  };

  const createMsbBond = async bondData => {
    try {
      isLoading.value = true;

      const newBond = await bondService.createMSB(
        bondData.bondName,
        bondData.code,
        bondData.msbType,
        bondData.yield,
        bondData.maturityDate,
        bondData.price,
        bondData.issueDate
      );

      msbBonds.value.push(newBond);
      return newBond;
    } catch (error) {
      console.error('통안채 생성 실패:', error);
      throw new Error('통안채 생성 중 오류가 발생했습니다.');
    } finally {
      isLoading.value = false;
    }
  };

  const updateMsbBond = async (bond, updates, baseDate = null) => {
    try {
      isLoading.value = true;

      await bondService.updateMsbBond(bond, updates, baseDate);

      // 로컬 데이터 업데이트
      const bondIndex = msbBonds.value.findIndex(b => b.code === bond.code);
      if (bondIndex !== -1) {
        msbBonds.value[bondIndex] = {
          ...msbBonds.value[bondIndex],
          ...updates,
        };
      }
    } catch (error) {
      console.error('통안채 수정 실패:', error);
      throw new Error('통안채 수정 중 오류가 발생했습니다.');
    } finally {
      isLoading.value = false;
    }
  };

  const getMsbTypeName = type => {
    return msbTypes.value[type] || type;
  };

  const getFilteredBonds = type => {
    return groupedBonds.value[type] || [];
  };

  const updateMsbType = async (bond, newType, baseDate = null) => {
    try {
      if (!baseDate) {
        baseDate = getTodayForAPI();
      }

      await bondService.updateMsbBond({
        code: bond.code,
        bondName: bond.bondName,
        yield: bond.yield,
        msbType: newType,
      }, baseDate);

      // 로컬 데이터 업데이트
      const bondIndex = msbBonds.value.findIndex(b => b.code === bond.code);
      if (bondIndex !== -1) {
        msbBonds.value[bondIndex] = {
          ...msbBonds.value[bondIndex],
          msbType: newType,
        };
      }
    } catch (error) {
      console.error('통안채 유형 변경 실패:', error);
      throw error;
    }
  };

  return {
    // State
    msbBonds,
    bondTypes,
    isLoading,

    // Computed
    msbTypes,
    typeOrder,
    groupedBonds,

    // Methods
    loadBondTypes,
    loadMsbBonds,
    createMsbBond,
    updateMsbBond,
    updateMsbType,
    getMsbTypeName,
    getFilteredBonds,
  };
}
